import React, { useState } from 'react';
import { Form, Select, Input, DatePicker, Alert, Space, Tag, Modal, message } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';
import WorkPlanService from '../services/Workplanservice';

const { Option } = Select;
const { TextArea } = Input;


const StatusUpdateModal = ({
    visible,
    selectedVisit,
    onClose,
    onSuccess
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState(null);

    // Check for duplicate when date changes
    const handleDateChange = async (date) => {
        if (!date || !selectedVisit) return;
        
        setCheckingDuplicate(true);
        setDuplicateWarning(null);
        
        try {
            const response = await WorkPlanService.checkDuplicateVisit({
                client_id: selectedVisit.client_id,
                scheduled_date: date.format('YYYY-MM-DD'),
                exclude_id: selectedVisit.id
            });
            
            if (response.code === 200 && response.result?.data?.has_duplicate) {
                setDuplicateWarning({
                    type: 'error',
                    message: 'Duplicate Schedule Detected',
                    description: `This facility already has a visit scheduled on ${date.format('DD MMM YYYY')}. Please choose a different date.`
                });
            } else {
                setDuplicateWarning(null);
            }
        } catch (error) {
            console.error('Error checking duplicate:', error);
        } finally {
            setCheckingDuplicate(false);
        }
    };

    const handleUpdateStatus = async () => {
        try {
            const values = await form.validateFields();
            
            // Check for duplicate before submitting
            if (duplicateWarning) {
                message.error('Cannot save: Duplicate schedule detected');
                return;
            }
            
            // Validate if marking as completed
            if (values.status === 'completed') {
                if (selectedVisit.assets_recorded_count === 0) {
                    Modal.confirm({
                        title: 'No Assets Recorded',
                        content: 'No assets have been recorded for this visit. Are you sure you want to mark it as completed?',
                        okText: 'Yes, Complete Anyway',
                        cancelText: 'Cancel',
                        onOk: async () => {
                            await performStatusUpdate({ ...values, force_complete: true });
                        }
                    });
                    return;
                }
            }
            
            await performStatusUpdate(values);
        } catch (error) {
            console.log('Validation failed:', error);
        }
    };

    const performStatusUpdate = async (values) => {
        setLoading(true);
        
        try {
            // Format data for API
            const updateData = {
                status: values.status,
                notes: values.notes || '',
                findings: values.findings || ''
            };
            
            // Add scheduled_date if changed
            if (values.scheduled_date) {
                updateData.scheduled_date = values.scheduled_date.format('YYYY-MM-DD');
            }
            
            // Add force_complete flag if present
            if (values.force_complete) {
                updateData.force_complete = true;
            }
            
            const response = await WorkPlanService.updateVisitStatus(
                selectedVisit.id,
                updateData
            );
            
            if (response.data.code === 200) {
                message.success(response.data.message || 'Status updated successfully');
                form.resetFields();
                setDuplicateWarning(null);
                onSuccess();
                onClose();
            } else {
                message.error(response.data.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            message.error(error.response?.data?.message || 'Failed to update status');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setDuplicateWarning(null);
        onClose();
    };

    if (!selectedVisit) return null;

    return (
        <Modal
            title={
                <Space>
                    <CalendarOutlined />
                    <span>Update Visit</span>
                    <Tag color="blue">{selectedVisit.facility_name}</Tag>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            onOk={handleUpdateStatus}
            okText="Update Visit"
            confirmLoading={loading}
            width={650}
            okButtonProps={{ disabled: !!duplicateWarning }}
        >
            {/* Visit Information */}
            <Alert
                message="Current Visit Information"
                description={
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div>
                            <strong>Facility:</strong> {selectedVisit.facility_name}
                        </div>
                        <div>
                            <strong>Region:</strong> <Tag>{selectedVisit.region}</Tag>
                        </div>
                        <div>
                            <strong>Current Date:</strong> {moment(selectedVisit.scheduled_date).format('DD MMM YYYY')}
                        </div>
                        <div>
                            <strong>Current Status:</strong> 
                            <Tag color={
                                selectedVisit.status === 'completed' ? 'success' :
                                selectedVisit.status === 'in_progress' ? 'processing' :
                                selectedVisit.status === 'cancelled' ? 'default' : 'warning'
                            }>
                                {selectedVisit.status}
                            </Tag>
                        </div>
                        <div>
                            <strong>Assets Recorded:</strong> 
                            {selectedVisit.assets_recorded_count > 0 ? (
                                <Tag color="success" icon={<CheckCircleOutlined />}>
                                    {selectedVisit.assets_recorded_count} assets
                                </Tag>
                            ) : (
                                <Tag color="warning" icon={<WarningOutlined />}>
                                    No assets recorded yet
                                </Tag>
                            )}
                        </div>
                    </Space>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            
            {/* Duplicate Warning */}
            {duplicateWarning && (
                <Alert
                    type={duplicateWarning.type}
                    message={duplicateWarning.message}
                    description={duplicateWarning.description}
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}
            
            {/* Update Form */}
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    status: selectedVisit.status,
                    scheduled_date: moment(selectedVisit.scheduled_date),
                    notes: selectedVisit.notes || '',
                    findings: selectedVisit.findings || ''
                }}
            >
                {/* Scheduled Date */}
                <Form.Item
                    label="Scheduled Date"
                    name="scheduled_date"
                    rules={[{ required: true, message: 'Please select a date' }]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        format="DD MMM YYYY"
                        onChange={handleDateChange}
                        loading={checkingDuplicate}
                    />
                </Form.Item>
                
                {/* Status */}
                <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: 'Please select a status' }]}
                >
                    <Select size="large">
                        <Option value="pending">
                            <Space>🟡 <span>Pending</span></Space>
                        </Option>
                        <Option value="in_progress">
                            <Space>🔵 <span>In Progress</span></Space>
                        </Option>
                        <Option value="completed">
                            <Space>
                                🟢 <span>Completed</span>
                                {selectedVisit.assets_recorded_count === 0 && (
                                    <Tag color="warning" size="small">No assets</Tag>
                                )}
                            </Space>
                        </Option>
                        <Option value="cancelled">
                            <Space>⚪ <span>Cancelled</span></Space>
                        </Option>
                    </Select>
                </Form.Item>
                
                {/* Notes */}
                <Form.Item label="Notes" name="notes">
                    <TextArea 
                        rows={3} 
                        placeholder="Add any notes about the visit..."
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
                
                {/* Findings (Required for Completion) */}
                <Form.Item
                    label="Findings"
                    name="findings"
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (getFieldValue('status') === 'completed' && !value) {
                                    return Promise.reject('Findings are required for completed visits');
                                }
                                return Promise.resolve();
                            }
                        })
                    ]}
                >
                    <TextArea 
                        rows={4} 
                        placeholder="Summarize key findings, issues identified, recommendations..."
                        maxLength={1000}
                        showCount
                    />
                </Form.Item>
            </Form>
            
            {/* Warning for completion without assets */}
            {form.getFieldValue('status') === 'completed' && 
             selectedVisit.assets_recorded_count === 0 && (
                <Alert
                    message="Warning: No Assets Recorded"
                    description="You are marking this visit as completed without recording any assets. Confirmation will be required."
                    type="warning"
                    showIcon
                />
            )}
        </Modal>
    );
};

export default StatusUpdateModal;